import { formatNumber } from '../../../formatNumber'
import { getTimeSeriesSummary } from '../getTimeSeriesSummary'
import { SummaryMode } from '../types'
import type { ChartSeries } from '../types'

/** A signed, compact change string, e.g. "+180", "−42", or "0". */
export function signedNumber(delta: number): string {
  if (delta === 0) return '0'
  return `${delta > 0 ? '+' : '−'}${formatNumber(Math.abs(delta))}`
}

/** One diverging bar: a series' net change over its range, plus the context the
 *  tooltip / data table read to describe it. Label, icon, and colour are NOT
 *  carried here — those resolve from {@link ChartSeriesContext} by `id`. */
export interface NetChangeRow {
  id: string
  /** Net change over the range (end − start); the value the bar plots. */
  delta: number
  /** Value at the range start (first non-null reading). */
  start: number
  /** Value at the range end (latest non-null reading). */
  end: number
  /** Percent change vs start; null when start <= 0 (avoids divide-by-zero / an
   *  undefined percent when the range didn't start from a positive baseline). */
  pct: number | null
  /** True when the series gained (or was flat); false when it lost. */
  isGain: boolean
}

/** Narrow an unknown Recharts tooltip payload to a {@link NetChangeRow}. The
 *  `payload[0].payload` Recharts hands the tooltip is untyped (`any`), so the
 *  tooltip guards on the fields it actually reads rather than trusting the shape. */
export function isNetChangeRow(value: unknown): value is NetChangeRow {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'delta' in value &&
    typeof value.id === 'string' &&
    typeof value.delta === 'number'
  )
}

/**
 * Derive one diverging net-change row per primary series, sorted by change desc
 * (gains first, losses last). Net change is `latest − first` of each series'
 * time series — reusing {@link getTimeSeriesSummary} in `LATEST` mode — so this
 * chart summarises cumulative (level) metrics like Followers. A series with no
 * readings is dropped rather than shown as a zero bar.
 */
export function buildNetChangeRows(series: ChartSeries[]): NetChangeRow[] {
  return series
    .filter((s) => s.kind === 'primary')
    .flatMap((s): NetChangeRow[] => {
      const { latest, change } = getTimeSeriesSummary(
        s.data,
        SummaryMode.LATEST,
      )
      if (latest == null) return []
      const end = latest
      const delta = change ?? 0
      const start = end - delta
      return [
        {
          id: s.id,
          delta,
          start,
          end,
          pct: start > 0 ? (delta / start) * 100 : null,
          isGain: delta >= 0,
        },
      ]
    })
    .toSorted((a, b) => b.delta - a.delta)
}
